import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toastUtils } from "../../../Utils/helper";
import { useTranslation } from "react-i18next";

import CommonModal from "../../../components/common/Modal/ModalCommon";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import LoadingSpinner from "../../../components/common/Loading/LoadingSpinner";
import { CustomSelect } from "../../../components/common/Select/CustomSelect";
import TaskQuest from "./TaskQuest";

import images from "../../../images";
import { formatDateToFakeMicroUTC } from "../../../Utils/helper";
import {
  // useFavoritesQuestMutation,
  useGetQuestDetailsPublicQuery,
  useGetQuestDetailsQuery,
  useLazyGetTakeQuestPracticeQuery,
  useUpdateUserInfoMutation,
} from "../../../services/apiSlice";

const QuestDetails = ({
  questId: propQuestId,
  onClose: propOnClose,
  onStartQuest,
}) => {
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const id = propQuestId || paramId;
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  const hasAccessToken = !!accessToken;

  const referralCode = searchParams.get("code");

  // --- Local State ---
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ age: "", gender: "" });
  const [errors, setErrors] = useState({ age: "", gender: "" });

  // --- Refs ---
  const validateInfomationModelRef = useRef();
  const detailModalRef = useRef(null);

  // --- API Hooks ---
  const {
    data: questDetailsData,
    isFetching: isFetchingAuth,
    // refetch: refetchQuestDetails,
  } = useGetQuestDetailsQuery(
    { quest_id: id },
    { skip: !hasAccessToken || !id, refetchOnMountOrArgChange: true },
  );

  const { data: questDetailsDataPublic, isFetching: isFetchingPublic } =
    useGetQuestDetailsPublicQuery(
      { quest_id: id },
      { skip: hasAccessToken || !id, refetchOnMountOrArgChange: true },
    );

  // const [favorites, { isLoading: isLoadingFavorites }] =
  //   useFavoritesQuestMutation();
  const [updateUserInfo] = useUpdateUserInfoMutation();

  const [onGetTakeQuestPractice, { isLoading: isLoadingPractice }] =
    useLazyGetTakeQuestPracticeQuery();

  const questData = hasAccessToken ? questDetailsData : questDetailsDataPublic;
  const isFetching = hasAccessToken ? isFetchingAuth : isFetchingPublic;
  const thumbnailPaths =
    questData?.data.thumbnails?.map((t) => t.file_path) || [];

  const optionsAge = useMemo(
    () => [
      { value: "", label: t("signUp.gender.gender") },
      { value: "male", label: t("signUp.gender.male") },
      { value: "female", label: t("signUp.gender.female") },
      { value: "maleIdentifying", label: t("signUp.gender.maleIdentifying") },
      {
        value: "femaleIdentifying",
        label: t("signUp.gender.femaleIdentifying"),
      },
      { value: "nonBinary", label: t("signUp.gender.nonBinary") },
      { value: "agender", label: t("signUp.gender.agender") },
      { value: "preferNotToSay", label: t("signUp.gender.preferNotToSay") },
    ],
    [t],
  );

  useEffect(() => {
    if (!isFetching && !isFetchingPublic && questData?.data) {
      detailModalRef.current?.openModal();
    }
  }, [isFetching, isFetchingPublic, questData]);

  useEffect(() => {
    if (id && hasAccessToken) {
      onGetTakeQuestPractice({ quest_id: id })
        .unwrap()
        .catch(() => {});
    }
  }, [id, onGetTakeQuestPractice, hasAccessToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, gender: selectedOption.value }));
    setErrors((prev) => ({ ...prev, gender: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.age || !formData.age.trim()) {
      newErrors.age = t("onBoard.ageRequired") || "Age is required";
    } else {
      const ageNum = parseInt(formData.age);
      if (ageNum < 13 || ageNum > 199) {
        newErrors.age =
          t("onBoard.ageInvalid") || "Age must be between 13 and 120";
      }
    }

    if (!formData.gender) {
      newErrors.gender = t("onBoard.genderRequired") || "Gender is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleValidateInformation = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const userData = { age: parseInt(formData.age), gender: formData.gender };
      const response = await updateUserInfo(userData).unwrap();

      if (response.status === 200) {
        setShowValidationModal(false);
        handleTakeQuest();
      }
    } catch (error) {
      console.error("Error updating info:", error);
      toastUtils.error(
        error?.data?.detail?.[0]?.msg || "Failed to update information",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTakeQuest = (e) => {
    e?.stopPropagation();

    // 1. Check Expiration
    const expireTime = questData?.data?.quest_business?.expire_time;
    if (expireTime) {
      const currentTime = new Date();
      const nowUTCISOString = formatDateToFakeMicroUTC(currentTime);
      const nowDate = new Date(nowUTCISOString.slice(0, 23));
      const questExpireTime = new Date(expireTime.slice(0, 23));

      if (nowDate > questExpireTime) {
        toastUtils.error(t("quest.questDetails.questExpired"));
        return;
      }
    }

    if (!hasAccessToken) {
      const queryParamsObj = {
        quest_id: id,
        location_id: questData?.data?.location_id || "",
      };
      if (referralCode) {
        queryParamsObj.code = referralCode;
      }

      const queryParams = new URLSearchParams(queryParamsObj).toString();

      navigate(`/invitation?${queryParams}`);
      return;
    }

    // if (userInfo?.data?.status !== "active") {
    //   setShowValidationModal(true);
    //   return;
    // }

    const {
      consumer_status: consumerStatus,
      number_handle_by_quota: handledQuota,
      slot_available: slotAvailable,
    } = questData?.data || {};
    const { quest_type: questType, quota_count: quotaCount } =
      questData?.data?.quest_business || {};

    if (questType === "REFERRAL" && consumerStatus !== "COMPLETED") {
      if (onStartQuest) {
        onStartQuest(id, questData?.data);
      } else {
        console.warn("onStartQuest handler not provided");
      }
      return;
    }

    if (
      consumerStatus === "COMPLETED" &&
      questType !== "SNAP" &&
      handledQuota === quotaCount
    ) {
      toastUtils.info(t("quest.questDetails.questCompleted"));
      return;
    }

    if (consumerStatus === "REVIEWING" && questType !== "SNAP") {
      toastUtils.info(t("quest.questDetails.questUnderReviewing"));
      return;
    }

    if (slotAvailable <= 0 && consumerStatus === "NOT_STARTED") {
      toastUtils.info(t("quest.questDetails.noSlotAvailable"));
      return;
    }

    if (onStartQuest) {
      onStartQuest(id, questData?.data);
    } else {
      console.warn("onStartQuest handler not provided");
    }
  };

  return (
    <>
      <LoadingCommon
        statusLoading={isFetching || (hasAccessToken && isLoadingPractice)}
      />

      <div
        className={`flex flex-col mx-auto w-full 
          `}
      >
        <TaskQuest
          id={questData?.data?.id}
          imagesNameShop={images.ImageNameShop}
          questName={questData?.data?.quest_business?.name || ""}
          shopName={questData?.data?.quest_business?.name || ""}
          branchName={""}
          description={questData?.data?.quest_business?.description || ""}
          questType={questData?.data?.quest_business?.quest_type || "INTRODUCE"}
          requirement={questData?.data.quest_business?.requirement || ""}
          estTime={questData?.data?.quest_business?.est_time}
          token={questData?.data?.quest_business?.token}
          expireIn={
            questData?.data?.participants &&
            Array.isArray(questData?.data?.participants) &&
            questData?.data?.participants.length > 0
              ? questData?.data?.participants[0].expired_at
              : null
          }
          quotaType={questData?.data?.quest_business?.quota_type ?? ""}
          expireTime={questData?.data?.quest_business?.expire_time || null}
          slotAvailable={questData?.data?.slot_available}
          maxSlots={hasAccessToken ? questData?.data?.max_slot : null}
          numberHandleByQuota={questData?.data?.number_handle_by_quota ?? 0}
          quotaCount={questData?.data?.quest_business?.quota_count ?? 0}
          isFavorited={questData?.data?.is_favorited || false}
          thumbnails={thumbnailPaths}
          businessId={questData?.data?.quest_business?.business_id}
          isEnable={
            hasAccessToken
              ? questData?.data?.is_enable
              : !(
                  questData?.data?.consumer_status === "FULL" ||
                  questData?.data?.slot_available === 0 ||
                  questData?.data?.quest_business?.status_biz === "EXPIRED" ||
                  questData?.data?.quest_business?.status_biz === "COMPLETED" ||
                  (questData?.data?.quest_business?.expire_time &&
                    new Date(questData.data.quest_business.expire_time + "Z") <
                      new Date())
                )
          }
          consumerStatus={
            hasAccessToken ? questData?.data?.consumer_status : "NOT_STARTED"
          }
          handleTaskQuest={handleTakeQuest}
          businessAvatar={
            questData?.data?.quest_business?.business?.avatar_data?.file_path
          }
          businessName={
            questData?.data?.quest_business.business.company_name || ""
          }
          address={questData?.data?.location.address || ""}
          name={questData?.data?.quest_business.name || ""}
          xp={questData?.data?.quest_business.xp || 0}
          locationId={questData?.data?.location.id || null}
          questData={questData?.data}
          isLoggedIn={hasAccessToken}
          onClose={propOnClose}
        />

        <CommonModal
          ref={validateInfomationModelRef}
          classNameText="text-[#201D1B] font-medium text-lg"
          canClickOverLay={false}
          showCloseIcon={true}
          closeIcon={images.CloseModalIconGray}
          classNameModal="relative !z-[10000]"
          isOpen={showValidationModal}
          onClose={() => setShowValidationModal(false)}
        >
          {showValidationModal && (
            <div className="flex flex-col justify-start items-start gap-[18px]">
              <h2 className="text-[#253FF6] text-4xl leading-[2.5rem] font-bold whitespace-pre-line">
                {t("onBoard.questMadeForYou")}
              </h2>
              <p className="text-xl font-medium text-[#253FF6] whitespace-pre-line">
                {t("onBoard.shareYourInfo")}
              </p>

              <div className="flex flex-col items-start w-full">
                <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 w-full">
                  <img
                    src={images.CalendarIcon2}
                    alt="Icon"
                    className="w-5 h-5 mr-3"
                  />
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder={t("onBoard.age")}
                    className="w-full px-4 py-1 text-base focus:outline-none focus:border-transparent"
                  />
                </div>
                {errors.age && (
                  <p className="text-red-500 text-sm mt-1">{errors.age}</p>
                )}
              </div>

              <div className="flex flex-col items-start w-full">
                <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 w-full">
                  <img
                    src={images.User3Icon}
                    alt="Icon"
                    className="w-5 h-5 mr-3"
                  />
                  <CustomSelect
                    options={optionsAge}
                    value={formData.gender}
                    onChange={handleSelectChange}
                    placeholder={t("onBoard.gender")}
                    icon={""}
                  />
                </div>
                {errors.gender && (
                  <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                )}
              </div>

              <button
                className={`w-full px-4 py-3 text-white rounded-lg text-lg font-medium transition-all ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#253FF6] hover:bg-[#1e32cc]"
                }`}
                onClick={handleValidateInformation}
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="xs" /> : t("base.next")}
              </button>
            </div>
          )}
        </CommonModal>
      </div>
    </>
  );
};

export default QuestDetails;
